import { Dialog, Transition } from "@headlessui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import {
  ComputerDesktopIcon,
  MoonIcon,
  SunIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { NavLink } from "react-router-dom";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const SIDEBAR_CONTAINER = "border-r border-[#2a3942] bg-[#111b21]";
const SIDEBAR_ACTIVE = "bg-[#2a3942] text-[#e9edef] ring-1 ring-[#00a884]/30";
const SIDEBAR_INACTIVE = "text-[#8696a0] hover:bg-[#2a3942] hover:text-[#e9edef]";
const SIDEBAR_CONTAINER_ADMIN_LIGHT = "border-r border-[#3a467a] bg-[#262f5e]";
const SIDEBAR_ACTIVE_ADMIN_LIGHT = "bg-white/20 text-white ring-1 ring-white/35";
const SIDEBAR_INACTIVE_ADMIN_LIGHT = "text-[#d7dcef] hover:bg-white/10 hover:text-white";
const THEME_STORAGE_KEY = "rp-theme";
const SmallSideBar = ({
  Fragment,
  navigation,
  user,
  location,
  sidebarOpen,
  setSidebarOpen,
}) => {

  const [showDetails, setshowDetails] = useState(false);
  const [themeMode, setThemeMode] = useState(
    () => localStorage.getItem(THEME_STORAGE_KEY) || "auto"
  );
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains("dark")
  );

  const { mailRooms } = useSelector((state) => state.mail);
  const isAdmin = Number(user?.role) === 1;
  const isAdminLight = isAdmin && !isDark;

  const getAutoDarkState = () => {
    const hour = new Date().getHours();
    return !(hour >= 6 && hour < 19);
  };

  const applyTheme = (mode) => {
    const shouldDark =
      mode === "dark" ? true : mode === "light" ? false : getAutoDarkState();
    document.documentElement.classList.toggle("dark", shouldDark);
    document.documentElement.style.colorScheme = shouldDark ? "dark" : "light";
    setIsDark(shouldDark);
  };

  const onThemeChange = (mode) => {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
    setThemeMode(mode);
    applyTheme(mode);
  };

  useEffect(() => {
    applyTheme(themeMode);
    if (themeMode !== "auto") return undefined;

    const timer = setInterval(() => {
      applyTheme("auto");
    }, 60000);
    return () => clearInterval(timer);
  }, [themeMode]);

  useEffect(() => {
    const syncThemeMode = () =>
      setThemeMode(localStorage.getItem(THEME_STORAGE_KEY) || "auto");
    window.addEventListener("storage", syncThemeMode);
    return () => window.removeEventListener("storage", syncThemeMode);
  }, []);
  const image = useMemo(() => {
    const hour = moment().hour();
    // Daytime: 8:00 to 18:59 (8-18) - white image
     return "https://www.repeathealth.com/wp-content/uploads/2024/02/Tag-Repeat-RGB-wit.png";
  }, []);


  const unreadMailCount = useMemo(
    () => (mailRooms || []).reduce((sum, item) => sum + Number(item?.sum_status || 0), 0),
    [mailRooms]
  );

  const ThemeIcon =
    themeMode === "light"
      ? SunIcon
      : themeMode === "dark"
      ? MoonIcon
      : ComputerDesktopIcon;

  return (
    <Transition.Root show={sidebarOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[70] lg:hidden"
        onClose={() => setSidebarOpen(false)}
      >
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/70" />
        </Transition.Child>

        <div className="fixed inset-0 flex">
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <Dialog.Panel className="relative mr-16 flex h-full w-full max-w-xs flex-1">
              <div
                className={classNames(
                  "flex min-h-0 min-w-0 grow flex-col gap-y-5 overflow-y-auto px-6 pb-2 ring-1 ring-black/20",
                  isAdminLight ? SIDEBAR_CONTAINER_ADMIN_LIGHT : SIDEBAR_CONTAINER
                )}
              >
            <div className="flex h-16 shrink-0 items-center justify-center">
              <img
                className="h-10 w-auto"
                src={image}
                alt="Repeathealth"
              />
            </div>
            <nav className="flex flex-1 flex-col">
              <ul className="-mx-2 flex-1 space-y-1">
              {navigation(user, unreadMailCount).map((item) => (
                <li key={item.name}>
                  <NavLink
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      classNames(
                        isActive || location.pathname.startsWith(item.href)
                          ? isAdminLight
                            ? SIDEBAR_ACTIVE_ADMIN_LIGHT
                            : SIDEBAR_ACTIVE
                          : isAdminLight
                          ? SIDEBAR_INACTIVE_ADMIN_LIGHT
                          : SIDEBAR_INACTIVE,
                        "group flex gap-x-3 rounded-lg px-3 py-2.5 text-[15px] leading-6 font-semibold"
                      )
                    }
                  >
                  {item.href == "/mailbox" ? (
                      <div className="flex-1 flex gap-x-3 relative">
                        {item?.count > 0 && <div className="bg-red-900 px-1.5 py-0.5 text-xs font-medium text-red-200 rounded absolute  -top-2 -left-3 p-1">
                          {item?.count}
                        </div>}

                        <item.icon
                          className="h-6 w-6 shrink-0"
                          aria-hidden="true"
                        />
                        <span className="relative">{item.name}</span>
                      </div>
                    ) : (
                      <div className="flex-1 flex gap-x-3">
                        <item.icon
                          className="h-6 w-6 shrink-0"
                          aria-hidden="true"
                        />
                        {item.name}
                      </div>
                    )}
                    {item?.children && (
                      <>
                        {showDetails ? (
                          <button
                            title="Sluit de details"
                            className="  p-1 rounded-full"
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              setshowDetails(false);
                            }}
                          >
                            {" "}
                            <ChevronDownIcon className="h-4 w-4 shrink-0" />{" "}
                          </button>
                        ) : (
                          <button
                            title="Open voor details"
                            className=" p-1 rounded-full"
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              setshowDetails(true);
                            }}
                          >
                            {" "}
                            <ChevronRightIcon className="h-4 w-4 shrink-0" />{" "}
                          </button>
                        )}
                      </>
                    )}
                  </NavLink>
                  {item?.children && showDetails && (
                    <ul className="ml-4 space-y-1">
                      {item.children.map((child) => (
                        <li key={child.name}>
                          <NavLink
                            to={child.href}
                            onClick={() => setSidebarOpen(false)}
                            className={({ isActive }) =>
                              classNames(
                                isActive
                                  ? isAdminLight
                                    ? SIDEBAR_ACTIVE_ADMIN_LIGHT
                                    : SIDEBAR_ACTIVE
                                  : isAdminLight
                                  ? SIDEBAR_INACTIVE_ADMIN_LIGHT
                                  : SIDEBAR_INACTIVE,
                                "group flex gap-x-3 rounded-lg px-3 py-2.5 text-[15px] leading-6 font-semibold"
                              )
                            }
                          >
                            <child.icon
                              className="h-6 w-6 shrink-0"
                              aria-hidden="true"
                            />
                            {child.name}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
              </ul>
              <div
                className={classNames(
                  "mt-3 rounded-xl border px-2.5 py-2",
                  isAdminLight ? "border-white/25 bg-white/5" : "border-[#2a3942] bg-[#202c33]"
                )}
              >
                <label className="flex items-center gap-2">
                  <ThemeIcon
                    className={classNames(
                      "h-4 w-4 shrink-0",
                      isAdminLight ? "text-[#d7dcef]" : "text-[#8696a0]"
                    )}
                    aria-hidden="true"
                  />
                  <span
                    className={classNames(
                      "text-xs font-semibold uppercase tracking-wide",
                      isAdminLight ? "text-[#d7dcef]" : "text-[#8696a0]"
                    )}
                  >
                    Thema
                  </span>
                  <select
                    value={themeMode}
                    onChange={(event) => onThemeChange(event.target.value)}
                    aria-label="Selecteer thema"
                    onClick={(event) => event.stopPropagation()}
                    className={classNames(
                      "ml-auto rounded-md border px-2 py-1 text-xs font-semibold uppercase outline-none",
                      isAdminLight
                        ? "border-white/25 bg-[#2f3658] text-white"
                        : "border-[#2a3942] bg-[#111b21] text-[#e9edef]"
                    )}
                  >
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                    <option value="auto">Auto</option>
                  </select>
                </label>
                {/* Keep a tiny quick reset for users who prefer explicit auto restore */}
                <button
                  type="button"
                  onClick={() => onThemeChange("auto")}
                  className={classNames(
                    "mt-2 w-full rounded-md border px-2 py-1 text-[11px] font-medium transition-colors",
                    isAdminLight
                      ? "border-white/25 text-[#d7dcef] hover:bg-white/10 hover:text-white"
                      : "border-[#2a3942] text-[#8696a0] hover:bg-[#2a3942] hover:text-[#e9edef]"
                  )}
                >
                  Reset auto
                </button>
              </div>
            </nav>
              </div>
              <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                <button
                  type="button"
                  className="-m-2.5 p-2.5"
                  onClick={() => setSidebarOpen(false)}
                >
                  <span className="sr-only">Close sidebar</span>
                  <XMarkIcon className="h-6 w-6 text-[#e9edef]" aria-hidden="true" />
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default SmallSideBar;
